import { server_url } from "@script/config";
import type { UserData } from "@vTypes/user-type.ts";
import { z } from "zod";
import {
  AuthError,
  isRosterNotEligible,
  parseAuthErrorPayload,
} from "./auth-error";
import {
  parseSsoMode,
  type DemoRole,
  type SsoMode,
} from "./sso-mode";
// const server_url = `${import.meta.env.VITE_API_BASE_URL}`;

const meditongTokenSchema = z.object({
  token: z.string().min(1),
  memberid: z.string().trim().min(1),
});

export type MeditongToken = z.infer<typeof meditongTokenSchema>;
export { AuthError } from "./auth-error";
export type { AuthErrorCode } from "./auth-error";

export async function login(body: URLSearchParams) {
  const response = await fetch(`${server_url}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
    credentials: "include",
  });
  if (!response.ok) {
    const payload = parseAuthErrorPayload(
      await response.json().catch(() => null)
    );
    const serverCode = payload.code ?? payload.error_code;
    const rosterNotEligible = isRosterNotEligible({
      status: response.status,
      code: serverCode,
      detail: payload.detail,
    });
    throw new AuthError({
      code: rosterNotEligible ? "ROSTER_NOT_ELIGIBLE" : "AUTH_FAILED",
      status: response.status,
      message: rosterNotEligible
        ? "AI근무표 이용 대상이 아닙니다."
        : "정보가 일치하지 않거나 서비스 이용자가 아닙니다.",
    });
  }
  return await response.json();
}

// 로그인 유저데이터
export async function loadLoginUser(
  signal?: AbortSignal
): Promise<UserData | null> {
  try {
    const response = await fetch(`${server_url}/auth/me`, {
      credentials: "include",
      signal,
    });
    if (response.status === 401) return null;
    if (!response.ok) return null;

    const result: UserData = await response.json();
    return result;
  } catch (error) {
    if (signal?.aborted) throw error;
    return null;
  }
}

export async function logout(signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(`${server_url}/auth/logout`, {
      method: "POST",
      credentials: "include",
      signal,
    });
    document.cookie = "onmaumLogin=; path=/; max-age=0";
    if (!response.ok) {
      console.error("logout error");
      return false;
    }
    // 본문은 쓰지 않는다 — 서버 세션 정리 여부는 상태코드로 판단한다.
    await response.json().catch(() => null);
    return true;
  } catch {
    // ★ 실패를 삼키면 안 된다. 호출부(TokenLogin)는 **서버 세션이 정리됐는지**로
    //   진입 여부를 정하므로, 네트워크·CORS·abort 어느 쪽이든 false 로 알려야 한다.
    console.error("logout error");
    return false;
  }
}

const MEDITONG_TOKEN_TIMEOUT_MS = 8000;

export async function fetchTokenFromMeditong(
  memberid: string,
  signal?: AbortSignal
): Promise<MeditongToken> {
  const requestedMemberid = memberid.trim();
  const dataKey = "meditong11**";
  const payload = new URLSearchParams();
  payload.append("dataKey", dataKey);
  payload.append("memberid", requestedMemberid);

  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) {
    controller.abort();
  } else {
    signal?.addEventListener("abort", abortFromCaller, { once: true });
  }
  const timer = window.setTimeout(
    () => controller.abort(),
    MEDITONG_TOKEN_TIMEOUT_MS
  );

  try {
    const response = await fetch(
      `https://www.meditong.com/renewal/airoster_token_api.asp`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded", // ASP에 적합한 타입
        },
        credentials: "include",
        body: payload.toString(),
        signal: controller.signal,
      }
    );
    if (!response.ok) {
      throw new AuthError({
        code: "TOKEN_FETCH_FAILED",
        status: response.status,
        message: "토큰을 가져올 수 없습니다.",
      });
    }

    const parsed = meditongTokenSchema.safeParse(await response.json());
    if (!parsed.success) {
      throw new AuthError({
        code: "TOKEN_FETCH_FAILED",
        message: "토큰 응답 형식이 올바르지 않습니다.",
      });
    }
    if (parsed.data.memberid !== requestedMemberid) {
      throw new AuthError({
        code: "TOKEN_FETCH_FAILED",
        message: "토큰 응답의 사용자 정보가 일치하지 않습니다.",
      });
    }
    return parsed.data;
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw new AuthError({
      code: "TOKEN_FETCH_FAILED",
      message: "토큰을 가져올 수 없습니다.",
    });
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", abortFromCaller);
  }
}

/** `/token/sso-mode` 대기 상한. 넘기면 normal 로 떨어뜨려 정상 로그인을 진행한다. */
const SSO_MODE_TIMEOUT_MS = 5000;

/**
 * SSO 진입 직후 역할 선택 모달을 띄울지 조회한다.
 *
 * ★ 조회에 실패하면 **normal 로 떨어뜨린다.** 시연 편의 기능 때문에 정상 진입이
 *   막히면 안 된다. 모달을 못 띄우면 자기 계정으로 들어갈 뿐이다.
 */
export async function fetchSsoMode(
  token: MeditongToken,
  signal?: AbortSignal
): Promise<SsoMode> {
  const formData = new FormData();
  formData.append("token", token.token);
  formData.append("MemberID", token.memberid);

  // ★ 반드시 타임아웃을 건다. 이 조회는 **시연 편의용 선택 기능**인데,
  //   서버가 연결만 받고 응답하지 않으면 `fetch` 는 영원히 기다리고
  //   **정상 사용자의 로그인까지 pending 에 갇힌다**. catch 폴백은 거부된
  //   요청만 잡을 뿐 응답 없는 연결은 못 잡는다.
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) {
    controller.abort();
  } else {
    signal?.addEventListener("abort", abortFromCaller, { once: true });
  }
  const timer = setTimeout(() => controller.abort(), SSO_MODE_TIMEOUT_MS);

  try {
    const response = await fetch(`${server_url}/token/sso-mode`, {
      method: "POST",
      body: formData,
      credentials: "include",
      signal: controller.signal,
    });
    if (!response.ok) return { mode: "normal" };

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) return { mode: "normal" };

    const result: unknown = await response.json();
    return parseSsoMode(result);
  } catch (error) {
    if (signal?.aborted) throw error;
    // 타임아웃(abort) 포함 — 어떤 실패든 normal 로 떨어뜨려 정상 로그인을 막지 않는다.
    return { mode: "normal" };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abortFromCaller);
  }
}

export async function tokenlogin(
  token: MeditongToken,
  demoRole?: DemoRole,
  signal?: AbortSignal
): Promise<unknown> {
  const formData = new FormData();
  formData.append("token", token.token);
  formData.append("MemberID", token.memberid);
  if (demoRole) {
    formData.append("demo_role", demoRole);
  }
  const response = await fetch(`${server_url}/token/login`, {
    method: "POST",
    body: formData,
    credentials: "include",
    signal,
  });
  const result: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const payload = parseAuthErrorPayload(result);
    const serverCode = payload.code ?? payload.error_code;
    const rosterNotEligible = isRosterNotEligible({
      status: response.status,
      code: serverCode,
      detail: payload.detail,
    });
    throw new AuthError({
      code: rosterNotEligible
        ? "ROSTER_NOT_ELIGIBLE"
        : "TOKEN_LOGIN_FAILED",
      status: response.status,
      message: rosterNotEligible
        ? "AI근무표 이용 대상이 아닙니다."
        : "토큰 로그인에 실패했습니다.",
    });
  }
  return result;
}
